import type { ComponentType, FC, ReactNode } from 'react'

import Image from 'next/image'
import { HeartHandshake, Hourglass, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'

interface Principle {
    numeral: string
    icon: ComponentType<{ className?: string }>
    title: string
    body: string
}

const PRINCIPLES: Array<Principle> = [
    {
        numeral: 'i.',
        icon: Hourglass,
        title: 'Built from the closing table',
        body: 'Closewell grew out of years spent chasing signatures and missed deadlines alongside agents. Every reminder it sends is one we wished someone had sent for us.'
    },
    {
        numeral: 'ii.',
        icon: ShieldCheck,
        title: 'Careful before clever',
        body: 'A missed date on a contract costs real money. Closewell checks with a person whenever it is unsure, and every action is written down on the deal.'
    },
    {
        numeral: 'iii.',
        icon: HeartHandshake,
        title: 'People first, always',
        body: 'Buying a home is one of the biggest moments in a life. We take the paperwork off your plate so you can be present for your clients.'
    }
]

const BADGE_TEXT = 'PEOPLE FIRST · CAREFUL ALWAYS · CLOSED WELL · '

/* Circular text seal that turns slowly around the arched window mark. */
const TurningSeal: FC = (): ReactNode => {
    return (
        <div className='relative size-full'>
            <svg viewBox='0 0 120 120' aria-hidden='true' className='slow-spin absolute inset-0 size-full text-foreground/70'>
                <defs>
                    <path id='about-seal-ring' d='M 60 60 m -44 0 a 44 44 0 1 1 88 0 a 44 44 0 1 1 -88 0' />
                </defs>

                <text fill='currentColor' fontSize='9.2' letterSpacing='2.4' style={{ fontFamily: 'var(--font-app-sans, sans-serif)' }}>
                    <textPath href='#about-seal-ring'>
                        {BADGE_TEXT}
                    </textPath>
                </text>
            </svg>

            <span className='absolute inset-[30%] flex items-center justify-center rounded-full bg-primary text-primary-foreground'>
                <LogoMark className='size-6' />
            </span>
        </div>
    )
}

/* A loose pen signature that draws itself in when its .reveal parent is shown. */
const Signature: FC = (): ReactNode => {
    return (
        <svg
            viewBox='0 0 220 70'
            fill='none'
            stroke='currentColor'
            strokeWidth={1.8}
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            className='logo-draw h-14 w-auto text-foreground'
        >
            <path pathLength={1} d='M8 46c10-22 22-34 30-30s-6 30-14 34 10-20 22-22 4 18 12 18 10-16 18-16-2 14 6 14 12-18 20-18' />
            <path pathLength={1} d='M112 30c-4 10-2 20 6 20s14-14 22-14-4 14 4 14 10-12 18-12 2 10 10 10 12-6 20-8' />
            <path pathLength={1} d='M14 60c40-6 110-8 196-4' />
        </svg>
    )
}

export const AboutSection: FC = (): ReactNode => {
    return (
        <section id='about' aria-labelledby='about-title' className='mx-auto max-w-6xl overflow-x-clip px-5 pt-28 sm:pt-36 lg:px-8'>
            <div className='grid gap-6 lg:grid-cols-[1.15fr_1fr] lg:items-end lg:gap-16'>
                <div>
                    <p className='reveal flex items-center gap-3 text-sm font-medium tracking-[0.14em] text-mist uppercase'>
                        <span aria-hidden='true' className='h-px w-10 bg-mist/60' />
                        About us
                    </p>

                    <h2 id='about-title' className='reveal reveal-d1 mt-5 font-heading text-[clamp(2.9rem,6.2vw,5.4rem)] leading-[0.94] tracking-[-0.03em] text-foreground'>
                        A quieter way
                        <em className='block pl-[0.6em]'>to close a home.</em>
                    </h2>
                </div>

                <p className='reveal reveal-d2 max-w-md text-[17px] leading-[1.6] text-muted-foreground lg:pb-3'>
                    Closewell is a small team of former transaction coordinators, agents and engineers. We built the coordinator we always wanted: one that never forgets a date, never lets a signature sit, and always lets you have the final word.
                </p>
            </div>

            <div className='mt-16 grid items-start gap-16 sm:mt-20 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20'>
                <div className='relative mx-auto w-full max-w-[420px] lg:sticky lg:top-28'>
                    <div data-reveal='scale' className='reveal'>
                        <div className='relative'>
                            <div aria-hidden='true' className='absolute inset-0 translate-x-4 translate-y-4 rounded-t-full rounded-b-[22px] border border-primary/35' />

                            <div className='group relative overflow-hidden rounded-t-full rounded-b-[22px] shadow-[0_40px_80px_-40px_rgba(60,48,24,0.45)]'>
                                <Image
                                    src='/images/about-window.jpg'
                                    alt='Painting of a quiet office corner at golden hour, with signed contracts, a fountain pen and a ring of house keys on a wooden table beneath an arched window onto olive trees'
                                    width={1600}
                                    height={2000}
                                    sizes='(min-width: 1024px) 420px, 90vw'
                                    className='h-auto w-full transition-[scale] duration-[1400ms] ease-out group-hover:scale-[1.05] motion-reduce:group-hover:scale-100'
                                />

                                <div aria-hidden='true' className='absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[rgba(35,34,30,0.35)] to-transparent' />
                            </div>
                        </div>
                    </div>

                    <div data-reveal='stamp' className='reveal reveal-d3 absolute -bottom-10 -left-4 sm:-left-10'>
                        <div className='w-52 -rotate-[4deg] rounded-[14px] border border-border bg-paper px-5 py-4 shadow-[0_24px_40px_-24px_rgba(60,48,24,0.5)] transition-[rotate] duration-500 ease-out hover:rotate-0 motion-reduce:hover:-rotate-[4deg]'>
                            <p className='text-[11px] font-medium tracking-[0.14em] text-mist uppercase'>
                                Field note
                            </p>

                            <p className='mt-1.5 font-heading text-[1.35rem] leading-[1.1] text-foreground italic'>
                                Every reminder is one we wished we had.
                            </p>
                        </div>
                    </div>

                    <div data-reveal='press' className='reveal reveal-d5 absolute -top-6 -right-3 size-28 sm:-right-8 sm:size-32'>
                        <div className='size-full rounded-full bg-background/90 shadow-[0_18px_34px_-20px_rgba(60,48,24,0.45)]'>
                            <TurningSeal />
                        </div>
                    </div>
                </div>

                <div className='pt-6 lg:pt-0'>
                    <p className='reveal font-heading text-[1.6rem] leading-tight text-foreground'>
                        Three promises <em>we keep on every deal</em>
                    </p>

                    <ol className='mt-8 border-t border-dashed border-border'>
                        {PRINCIPLES.map(({ numeral, icon: Icon, title, body }, index) => (
                            <li key={title} className='border-b border-dashed border-border'>
                                <div data-reveal='right' className={cn('reveal', `reveal-d${index + 1}`)}>
                                    <div className='group grid grid-cols-[3.25rem_1fr] gap-x-4 rounded-[18px] px-3 py-7 transition-colors duration-500 hover:bg-paper sm:grid-cols-[4.5rem_1fr_auto] sm:px-5'>
                                        <span className='font-heading text-[2.6rem] leading-none text-pebble italic transition-colors duration-500 group-hover:text-primary sm:text-[3.4rem]'>
                                            {numeral}
                                        </span>

                                        <div>
                                            <h3 className='font-heading text-[1.75rem] leading-tight tracking-[-0.01em] text-foreground'>
                                                {title}
                                            </h3>

                                            <p className='mt-2 max-w-lg text-[15px] leading-relaxed text-muted-foreground'>
                                                {body}
                                            </p>
                                        </div>

                                        <span className='hidden size-11 items-center justify-center self-center rounded-full border border-border bg-background text-primary transition-[rotate,background-color,color] duration-500 ease-out group-hover:-rotate-12 group-hover:bg-primary group-hover:text-primary-foreground motion-reduce:group-hover:rotate-0 sm:flex'>
                                            <Icon className='size-5' />
                                        </span>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div className='reveal reveal-d4 mt-10 flex flex-wrap items-end gap-x-6 gap-y-3 px-3 sm:px-5'>
                        <Signature />

                        <div className='pb-2'>
                            <p className='text-[15px] font-medium text-foreground'>
                                The Closewell team
                            </p>

                            <p className='text-[13px] text-mist'>
                                Coordinators, agents and engineers
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
